import type { TeamTask } from "@/components/fretum/types";

import { dayKey, toLocalDate } from "../../mobile/mobileModel";
import { taskHref } from "../../tasks/taskKind";
import type { Deal, Task } from "../../types";

/** Pure data helpers of the desktop shell (sidebar status card, task badges). */

export interface MonthGoal {
  /** Sum of the amounts of the won deals closing this month. */
  won: number;
  /** Sum of the amounts of every deal closing this month, except lost ones. */
  expected: number;
  /** won / expected, clamped to 0..1 (0 when nothing is expected). */
  ratio: number;
  /** Deals counted in `expected`. */
  count: number;
}

/** First day of the month of `now` and first day of the next month (local). */
export const monthBounds = (now: Date): { start: Date; end: Date } => ({
  start: new Date(now.getFullYear(), now.getMonth(), 1),
  end: new Date(now.getFullYear(), now.getMonth() + 1, 1),
});

/**
 * Monthly goal of the status card. A deal belongs to the month through its
 * `expected_closing_date`; the goal is what the pipeline expects to close this
 * month (open + won deals, lost and archived ones excluded) and the progress is
 * the part already won (stage in `wonStages`, i.e. the configured
 * dealPipelineStatuses: won and its delivery stages).
 */
export const computeMonthGoal = (
  deals: Pick<
    Deal,
    "amount" | "stage" | "expected_closing_date" | "archived_at"
  >[],
  wonStages: string[],
  now: Date,
): MonthGoal => {
  const { start, end } = monthBounds(now);
  const inMonth = deals.filter((deal) => {
    if (
      deal.archived_at ||
      deal.stage === "lost" ||
      !deal.expected_closing_date
    )
      return false;
    const closing = toLocalDate(deal.expected_closing_date);
    return closing >= start && closing < end;
  });
  const amount = (deal: { amount: number }) =>
    Number.isFinite(deal.amount) ? deal.amount : 0;
  const expected = inMonth.reduce((sum, deal) => sum + amount(deal), 0);
  const won = inMonth
    .filter((deal) => wonStages.includes(deal.stage))
    .reduce((sum, deal) => sum + amount(deal), 0);
  return {
    won,
    expected,
    ratio: expected > 0 ? Math.min(1, Math.max(0, won / expected)) : 0,
    count: inMonth.length,
  };
};

export interface DueItem {
  id: string;
  title: string;
  due: Date;
  kind: "task" | "team_task";
  href: string;
  state: "overdue" | "today" | "upcoming";
}

export interface MyTasksSummary {
  overdue: number;
  dueToday: number;
  /** Open items due up to `horizonDays` ahead (overdue first), by due date. */
  items: DueItem[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Open tasks of the current user: contact tasks (`tasks`, not done) and team
 * tasks (`team_tasks` assigned to them, status other than done), bucketed by
 * due day against `now` (local days).
 */
export const summarizeMyTasks = (
  tasks: Pick<Task, "id" | "text" | "due_date" | "done_date" | "contact_id">[],
  teamTasks: Pick<TeamTask, "id" | "title" | "due_date" | "status">[],
  now: Date,
  horizonDays = 7,
): MyTasksSummary => {
  const today = dayKey(now);
  const horizon = dayKey(new Date(now.getTime() + horizonDays * DAY_MS));
  const stateOf = (due: Date): DueItem["state"] => {
    const key = dayKey(due);
    return key < today ? "overdue" : key === today ? "today" : "upcoming";
  };

  const fromTasks = tasks
    .filter((task) => !task.done_date && task.due_date)
    .map((task) => {
      const due = toLocalDate(task.due_date);
      return {
        id: `task-${task.id}`,
        title: task.text,
        due,
        kind: "task" as const,
        href: taskHref(task),
        state: stateOf(due),
      };
    });
  const fromTeam = teamTasks
    .filter((task) => task.status !== "done" && task.due_date)
    .map((task) => {
      const due = toLocalDate(task.due_date as string);
      return {
        id: `team-${task.id}`,
        title: task.title,
        due,
        kind: "team_task" as const,
        href: `/team_tasks/${task.id}`,
        state: stateOf(due),
      };
    });

  const all = [...fromTasks, ...fromTeam].filter(
    (item) => !Number.isNaN(item.due.getTime()),
  );
  return {
    overdue: all.filter((item) => item.state === "overdue").length,
    dueToday: all.filter((item) => item.state === "today").length,
    items: all
      .filter((item) => dayKey(item.due) <= horizon)
      .sort((left, right) => left.due.getTime() - right.due.getTime()),
  };
};

/** First name of an identity's full name ("" when unknown). */
export const firstName = (fullName: string | undefined | null): string =>
  (fullName ?? "").trim().split(/\s+/)[0] ?? "";

/** Initials for the avatar fallback ("JR" for "Jorge Rosario"). */
export const initials = (fullName: string | undefined | null): string =>
  (fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
